import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, Briefcase, Users, Banknote, Building2, ExternalLink, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const SearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/search', { params: { q: query } });
      setResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const totalResults = results
    ? (results.entrepreneurs?.length || 0) + (results.staff?.length || 0) + (results.loans?.length || 0) + (results.branches?.length || 0)
    : 0;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="search-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">Global Search</h1>
        <p className="text-sm text-slate-600">Search by name, mobile, loan ID, branch, enrollment number, enterprise...</p>
      </div>

      <div className="max-w-3xl mb-6">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Type name, mobile, loan ID, branch..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 h-11"
              autoFocus
              data-testid="search-input"
            />
          </div>
          <Button type="submit" disabled={loading} data-testid="search-button" className="h-11 px-6 bg-emerald-700 hover:bg-emerald-800 text-white">
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </form>
      </div>

      {results && (
        <div className="mb-4">
          <p className="text-sm text-slate-500">{totalResults} result(s) found for "<span className="font-medium text-slate-700">{query}</span>"</p>
        </div>
      )}

      {results && (
        <div className="space-y-5">

          {/* Entrepreneurs */}
          {results.entrepreneurs?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-emerald-50">
                <Briefcase className="w-4 h-4 text-emerald-700" />
                <h2 className="text-sm font-semibold text-emerald-900">Entrepreneurs ({results.entrepreneurs.length})</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {results.entrepreneurs.map((ent) => (
                  <div key={ent.id} className="px-4 py-3 hover:bg-slate-50 transition-colors" data-testid="entrepreneur-result">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/entrepreneurs/${ent.id}`} className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 hover:underline" data-testid={`ent-profile-${ent.id}`}>
                            {ent.name}
                          </Link>
                          {ent.enrollment_no && <span className="text-xs text-slate-400">{ent.enrollment_no}</span>}
                          {ent.branch_code && <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{ent.branch_code}</span>}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          {ent.mobile && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{ent.mobile}</span>}
                          {ent.enterprise?.name && <span>{ent.enterprise.name} ({ent.enterprise.operation_type})</span>}
                          {ent.branch_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ent.branch_name}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {ent.loans?.map((loan) => (
                          <Link
                            key={loan.id}
                            to={`/loans/${loan.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border font-medium hover:shadow-sm transition-shadow"
                            style={{
                              background: loan.status === 'active' ? '#ecfdf5' : loan.status === 'closed' ? '#f1f5f9' : '#fef2f2',
                              borderColor: loan.status === 'active' ? '#a7f3d0' : loan.status === 'closed' ? '#cbd5e1' : '#fecaca',
                              color: loan.status === 'active' ? '#047857' : loan.status === 'closed' ? '#475569' : '#dc2626'
                            }}
                            data-testid={`loan-link-${loan.loan_id}`}
                          >
                            <Banknote className="w-3 h-3" />
                            {loan.loan_id} (₹{loan.loan_amount?.toLocaleString('en-IN')})
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        ))}
                        {(!ent.loans || ent.loans.length === 0) && (
                          <span className="text-xs text-slate-400 italic">No loans</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loans */}
          {results.loans?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-purple-50">
                <Banknote className="w-4 h-4 text-purple-700" />
                <h2 className="text-sm font-semibold text-purple-900">Loans ({results.loans.length})</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {results.loans.map((loan) => (
                  <div key={loan.id} className="px-4 py-3 hover:bg-slate-50 transition-colors" data-testid="loan-result">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/loans/${loan.id}`} className="text-sm font-semibold text-purple-700 hover:text-purple-900 hover:underline" data-testid={`loan-detail-${loan.loan_id}`}>
                            {loan.loan_id}
                          </Link>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            loan.status === 'active' ? 'bg-emerald-100 text-emerald-800' : loan.status === 'closed' ? 'bg-slate-100 text-slate-700' : 'bg-red-100 text-red-800'
                          }`}>{loan.status}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">{loan.loan_type}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span>Entrepreneur: <Link to="/entrepreneurs" className="text-emerald-600 hover:underline">{loan.entrepreneur_name}</Link></span>
                          <span>₹{loan.loan_amount?.toLocaleString('en-IN')}</span>
                          {loan.branch_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{loan.branch_name}</span>}
                        </div>
                      </div>
                      <Link to={`/loans/${loan.id}`} className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                        View Details <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Staff */}
          {results.staff?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-blue-50">
                <Users className="w-4 h-4 text-blue-700" />
                <h2 className="text-sm font-semibold text-blue-900">Staff ({results.staff.length})</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {results.staff.map((member) => (
                  <div key={member.id} className="px-4 py-3 hover:bg-slate-50 transition-colors" data-testid="staff-result">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <Link to={`/staff/${member.id}`} className="text-sm font-semibold text-blue-700 hover:text-blue-900 hover:underline">
                            {member.name}
                          </Link>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{member.staff_type}</span>
                          {member.branch_code && <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{member.branch_code}</span>}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          {member.mobile && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{member.mobile}</span>}
                          {member.branch_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{member.branch_name}</span>}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        member.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
                      }`}>{member.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Branches */}
          {results.branches?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-amber-50">
                <Building2 className="w-4 h-4 text-amber-700" />
                <h2 className="text-sm font-semibold text-amber-900">Branches ({results.branches.length})</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {results.branches.map((branch) => (
                  <div key={branch.id} className="px-4 py-3 hover:bg-slate-50 transition-colors" data-testid="branch-result">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Link to="/branches" className="text-sm font-semibold text-amber-700 hover:text-amber-900 hover:underline">
                            {branch.name}
                          </Link>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{branch.code}</span>
                        </div>
                        {branch.address && <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{branch.address}</p>}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        branch.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
                      }`}>{branch.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {totalResults === 0 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No results found for "<span className="font-medium">{query}</span>"</p>
              <p className="text-xs text-slate-400 mt-1">Try searching by name, mobile number, loan ID, branch name, or enrollment number</p>
            </div>
          )}
        </div>
      )}

      {!results && (
        <div className="text-center py-16">
          <Search className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">Search across the entire portal</p>
          <p className="text-sm text-slate-400 mt-1">Entrepreneurs, Staff, Loans, Branches - all in one place</p>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
